/**
 * useAuth — React hook for Firebase authentication state
 *
 * Provides user object, loading state, auth actions, and subscription status.
 * Calls the auth worker's /verify endpoint on login to provision user + get subscription info.
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  auth,
  onAuthStateChanged,
  signInWithGoogle,
  signInWithEmail,
  signUpWithEmail,
  signOut,
  getIdToken,
} from '../utils/firebase.js';

const AUTH_WORKER_URL = import.meta.env.VITE_AUTH_WORKER_URL || 'https://vectrax-auth.thegpc806.workers.dev';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const authValue = useAuthInternal();
  return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>;
}

export default function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// Non-throwing variant for surfaces that must render outside an AuthProvider
// (isolated component tests, static/degraded renders). Returns a signed-out
// stub — never a token — so callers gate writes on `user` / `getIdToken`.
const SIGNED_OUT = { user: null, loading: false, getIdToken: null, subscriptionResolved: true };
export function useAuthOptional() {
  return useContext(AuthContext) || SIGNED_OUT;
}

function useAuthInternal() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState({
    subscribed: false,
    plan: 'free',
    status: 'none',
    expiresAt: null,
  });
  // True once the subscription/plan has settled (success or failure). Guards must wait on this
  // before deciding entitlements, else a paying user flashes the upgrade wall (UX-02).
  const [subscriptionResolved, setSubscriptionResolved] = useState(false);

  // Verify token with auth worker and get subscription status
  const verifyWithBackend = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      setSubscription({ subscribed: false, plan: 'free', status: 'none', expiresAt: null });
      setSubscriptionResolved(true);
      return;
    }
    setSubscriptionResolved(false);
    try {
      const token = await firebaseUser.getIdToken();
      const resp = await fetch(`${AUTH_WORKER_URL}/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (resp.ok) {
        const data = await resp.json();
        setSubscription({
          subscribed: data.subscribed,
          plan: data.plan,
          status: data.subscriptionStatus,
          expiresAt: data.subscriptionExpiresAt,
        });
      }
    } catch (err) {
      console.error('Backend verification failed:', err);
    } finally {
      // Fail-closed: on a verify error the plan stays 'free' but we mark resolved so the UI
      // doesn't hang on a skeleton forever.
      setSubscriptionResolved(true);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(async (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
      await verifyWithBackend(firebaseUser);
    });
    return unsubscribe;
  }, [verifyWithBackend]);

  const handleSignInWithGoogle = useCallback(async () => {
    const result = await signInWithGoogle();
    await verifyWithBackend(result.user);
    return result;
  }, [verifyWithBackend]);

  const handleSignInWithEmail = useCallback(async (email, password) => {
    const result = await signInWithEmail(email, password);
    await verifyWithBackend(result.user);
    return result;
  }, [verifyWithBackend]);

  const handleSignUpWithEmail = useCallback(async (email, password) => {
    const result = await signUpWithEmail(email, password);
    await verifyWithBackend(result.user);
    return result;
  }, [verifyWithBackend]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    setSubscription({ subscribed: false, plan: 'free', status: 'none', expiresAt: null });
    setSubscriptionResolved(true);
  }, []);

  const refreshSubscription = useCallback(async () => {
    if (user) await verifyWithBackend(user);
  }, [user, verifyWithBackend]);

  return {
    user,
    loading,
    subscription,
    subscriptionResolved,
    signInWithGoogle: handleSignInWithGoogle,
    signInWithEmail: handleSignInWithEmail,
    signUpWithEmail: handleSignUpWithEmail,
    signOut: handleSignOut,
    getIdToken,
    refreshSubscription,
  };
}
